<template>
  <div class="approval-list-container">
    <CustomLoader v-if="isBusy" :width="70"></CustomLoader>
    <b-card
      no-body
      class="border-0 shadow-sm"
      style="border-radius: 8px; overflow: hidden;"
    >
      <b-tabs v-model="activeTab" card pills @input="onTabChange">
        <b-tab title="All"></b-tab>
        <b-tab title="Pending"></b-tab>
        <b-tab title="Approved"></b-tab>
        <b-tab title="Rejected"></b-tab>
      </b-tabs>

      <b-card-header class="bg-white border-bottom p-3">
        <div class="d-flex justify-content-between align-items-center mb-0">
          <h4 class="mb-0 text-primary font-weight-bold">
            <i class="fa fa-ship mr-2"></i>SLINE DISPUTE DASHBOARD
          </h4>
          <div class="d-flex align-items-center">
            <b-input-group class="mr-3" style="width: 300px;">
              <b-input-group-prepend is-text class="bg-light border-right-0">
                <i class="fa fa-search text-muted"></i>
              </b-input-group-prepend>
              <b-form-input
                v-model="filter"
                placeholder="Search Job No, Inv No..."
                class="border-left-0 bg-light"
                @input="debounceSearch"
              />
            </b-input-group>
            <b-button variant="success" class="mr-2" @click="exportExcel" :disabled="exportLoading">
              <i class="fa fa-file-excel-o"></i> {{ exportLoading ? '...' : 'Export' }}
            </b-button>
            <b-button variant="outline-primary" @click="getData">
              <i class="fa fa-refresh"></i> Refresh
            </b-button>
            <b-button
              v-b-toggle.filter-collapse
              variant="outline-secondary"
              class="ml-2"
            >
              <i class="fa fa-filter"></i> Filters
            </b-button>
          </div>
        </div>

        <b-collapse id="filter-collapse" v-model="filtersVisible" class="mt-3">
          <b-card class="bg-light border-0">
            <b-row>
              <b-col md="4" class="mb-3">
                <label class="small text-muted font-weight-bold">Date Range</label>
                <date-range-picker
                  v-model="dateRange"
                  :startDate="dateRange.startDate"
                  :endDate="dateRange.endDate"
                  @update="onFilterChange"
                  class="w-100"
                >
                  <template slot="input" slot-scope="picker">
                    <div class="d-flex align-items-center form-control">
                      <i class="fa fa-calendar mr-2 text-muted"></i>
                      <span v-if="picker.startDate && picker.endDate">
                        {{ moment(picker.startDate).format('DD/MM/YYYY') }} - {{ moment(picker.endDate).format('DD/MM/YYYY') }}
                      </span>
                      <span v-else class="text-muted">Select Date Range</span>
                    </div>
                  </template>
                </date-range-picker>
              </b-col>
              <b-col md="2" class="mb-3">
                <label class="small text-muted font-weight-bold">Job Type</label>
                <model-list-select
                  :list="jobTypeOptions"
                  v-model="filters.jobType"
                  option-value="value"
                  option-text="text"
                  placeholder="All"
                  @input="onFilterChange"
                />
              </b-col>
              <b-col md="2" class="mb-3">
                <label class="small text-muted font-weight-bold">Transport</label>
                <model-list-select
                  :list="transportOptions"
                  v-model="filters.transportType"
                  option-value="value"
                  option-text="text"
                  placeholder="All"
                  @input="onFilterChange"
                />
              </b-col>
              <b-col md="2" class="d-flex align-items-end mb-3">
                <b-button variant="outline-danger" size="sm" @click="clearFilters" block>
                  Reset
                </b-button>
              </b-col>
            </b-row>
          </b-card>
        </b-collapse>
      </b-card-header>

      <b-card-body class="p-0">
        <b-table
          :items="items"
          :fields="fields"
          responsive
          show-empty
          hover
          head-variant="light"
          class="mb-0 custom-table"
        >
          <template slot="empty">
            <div class="text-center text-muted my-5">
              <i class="fa fa-inbox fa-3x mb-3"></i>
              <p>No records found.</p>
            </div>
          </template>

          <template slot="SN" slot-scope="row">
            <span>{{ row.index + 1 + (currentPage - 1) * perPage }}</span>
          </template>
          <template slot="invoiceDetails" slot-scope="row">
            <div class="cell-content" v-if="row.item.invoice_details">
              <div>
                <strong>Inv No:</strong> 
                <a 
                  v-if="row.item.invoice_details.invUpload" 
                  :href="String(row.item.invoice_details.invUpload).startsWith('http') ? row.item.invoice_details.invUpload : `${docURL}${row.item.jobNo}/${row.item.invoice_details.invUpload}`"
                  target="_blank"
                  class="text-primary font-weight-bold"
                >
                  {{ row.item.invoice_details.invNo }}
                </a>
                <span v-else>{{ row.item.invoice_details.invNo }}</span>
              </div>
              <div><strong>Inv Date:</strong> {{ row.item.invoice_details.invDate }}</div>
              <div class="text-muted small"><strong>Pay Req Date:</strong> {{ row.item.invoice_details.paymentReqDate }}</div>
              <div class="text-muted small"><strong>Inv Category:</strong> {{ row.item.invoice_details.invCategory }}</div>
              <div class="text-muted small"><strong>Inv Type:</strong> {{ row.item.invoice_details.invType }}</div>
              <div class="text-muted small"><strong>Dispute Reason:</strong> {{ row.item.invoice_details.disputeReason }}</div>
              <div class="text-muted small"><strong>Inv Amount:</strong> {{ row.item.invoice_details.billingAmount }}</div>
              <div class="text-muted small"><strong>Billing Amount:</strong> <span class="text-success">{{ row.item.invoice_details.resolvedAmount }}</span></div>
              <div class="text-muted small"><strong>Dispute Status:</strong> {{ row.item.invoice_details.status }}</div>
              <div class="text-muted small"><strong>To be Billed:</strong> {{ row.item.invoice_details.category }}</div>
              <div class="text-muted small"><strong>Remark:</strong> {{ row.item.invoice_details.remark }}</div>
            </div>
            <div v-else class="text-muted italic small">No invoice data</div>
          </template> 
          <template slot="jobDetails" slot-scope="row">
            <div class="cell-content">
              <div><strong>Job No:</strong> <span class="text-primary font-weight-bold">{{ row.item.jobNo }}</span></div>
              <div><strong>Job Date:</strong> <span class="text-primary font-weight-bold">{{ row.item.jobDate }}</span></div>
              <div class="text-muted small"><strong>Type:</strong> {{ row.item.rsa_job_type }} / {{ row.item.rsa_transport_type }}</div>
              <div class="text-muted small"><strong>Mode:</strong> {{ row.item.mode }}</div>
              <div class="text-muted small"><strong>Vessel:</strong> {{ row.item.vesselVoyage }}</div>
            </div>
          </template>
          <template slot="locDetails" slot-scope="row">
            <div class="cell-content small">
              <div><strong>POR:</strong> {{ row.item.por }}</div>
              <div><strong>POL:</strong> {{ row.item.pol }}</div>
              <div><strong>POD:</strong> {{ row.item.pod }}</div>
              <div><strong>FPOD:</strong> {{ row.item.fpod }}</div>
            </div>
          </template>
          <template slot="otherDetails" slot-scope="row">
            <div class="cell-content small">
              <div><strong>Client:</strong> {{ row.item.clientName }}</div>
              <div><strong>SLine:</strong> {{ row.item.shippingLine }}</div>
              <div><strong>Vessel / Voy:</strong> {{ row.item.vessel }} / {{ row.item.voyage }}</div>
              <div><strong>MBL / HBL:</strong> {{ row.item.mblNo }} / {{ row.item.hblNo }}</div>
              <div><strong>SOB Date:</strong> {{ row.item.sobDate }}</div>
              <div><strong>MBL Date / Rel:</strong> {{ row.item.mblDate }} / {{ row.item.mblReleaseDate }}</div>
            </div>
          </template>

          

          <template slot="people" slot-scope="row">
            <div class="cell-content">
              <div class="mb-1"><strong>Req:</strong> {{ row.item.requesterName }}</div>
              <div class="text-muted small"><strong>App:</strong> {{ row.item.approverName || 'Pending Assignment' }}</div>
            </div>
          </template>

          <template slot="status" slot-scope="row">
            <div class="text-center">
              <b-badge :variant="getStatusVariant(row.item.rsa_approval_status)" class="px-2 py-1">
                {{ row.item.rsa_approval_status }}
              </b-badge>
              <div class="text-muted extra-small mt-1">{{ row.item.rsa_created_at_fmt }}</div>
            </div>
          </template>

          <template slot="actions" slot-scope="row">
            <div class="d-flex justify-content-center">
               <b-button 
                size="sm" 
                variant="info" 
                @click="viewDetails(row.item)" 
                class="mr-1"
                v-b-tooltip.hover title="View Job Details"
              >
                <i class="fa fa-eye"></i>
              </b-button>
              <template v-if="row.item.rsa_approval_status === 'PENDING'">
                <b-button size="sm" variant="success" @click="confirmAction(row.item, 'APPROVE')" class="mr-1">
                  <i class="fa fa-check"></i>
                </b-button>
                <b-button size="sm" variant="danger" @click="confirmAction(row.item, 'REJECT')">
                  <i class="fa fa-times"></i>
                </b-button>
              </template>
            </div>
          </template>
        </b-table>
      </b-card-body>

      <b-card-footer class="bg-white border-top py-3">
        <div class="d-flex justify-content-between align-items-center">
          <div class="text-muted small">
            Showing {{ items.length > 0 ? (currentPage - 1) * perPage + 1 : 0 }} to {{ Math.min(currentPage * perPage, totalRows) }} of {{ totalRows }} entries
          </div>
          <b-pagination
            v-model="currentPage"
            :total-rows="totalRows"
            :per-page="perPage"
            align="right"
            class="mb-0 custom-pagination"
            @change="onPageChange"
            pills
          ></b-pagination>
        </div>
      </b-card-footer>
    </b-card>

    <!-- Confirmation Modal -->
    <b-modal 
        id="action-confirm-modal" 
        ref="action-confirm-modal"
        :title="confirmData.title" 
        :ok-variant="confirmData.variant" 
        @ok="handleAction"
        ok-title="YES"
        cancel-title="NO"
        centered
        size="sm"
    >
        <p class="my-4">{{ confirmData.message }}</p>
    </b-modal>

    <!-- Job Details Modal -->
    <b-modal
        id="view-job-details-modal"
        ref="view-job-details-modal"
        title="Job Summary & Documents"
        size="xl"
        hide-footer
        centered
    >
        <div v-if="selectedJob">
            <doc-form :allModuleIdProp="selectedJob.fk_module_id" :JOBSERIES="selectedJob.jobNo" />
        </div>
    </b-modal>
  </div>
</template>

<script>
import SlineApprovalService from "../../services/SlineApprovalService";
import CustomLoader from "../common/customLoader.vue";
import { DateFormat, selectDateFormat } from "../../helper/filters";
import { globalPermissionStatus, lastVisitPage } from "../../helper/collection";
import { ModelListSelect } from "vue-search-select";
import DateRangePicker from "vue2-daterange-picker";
import "vue2-daterange-picker/dist/vue2-daterange-picker.css";
import moment from "moment";
import DocForm from "../allModuleForm/docLibrary.vue";

import { URL_BASE_DOC } from "../../helper/constant";

import {
  exportSlineApprovalListToExcel
} from "../../helper/ExcelExportHelper";

export default {
  name: "SlineApprovalList",
  components: {
    CustomLoader,
    ModelListSelect,
    DateRangePicker,
    DocForm,
  },
  beforeMount() {
    lastVisitPage(this.$router.history.current.fullPath);
    const storePromises = [
      this.$store.dispatch(
        "common/setAllUserList",
        JSON.stringify({
          itemCode: "GET_USERLIST_Data",
        })
      ),
      this.$store.dispatch(
        "common/setAllBranchList",
        JSON.stringify({
          filter: "BranchList",
          active: "all",
        })
      ),
      this.$store.dispatch("common/setAllAgentColoaderList"),
      this.$store.dispatch(
        "common/setAllClientList",
        JSON.stringify({ itemCode: "ClientList" })
      ),
      this.$store.dispatch(
        "common/setAllShippingLineList",
        JSON.stringify({ itemCode: "ShippingLineList" })
      ),
      ];
     // Function to introduce a delay
    const delay = (ms) => new Promise((resolve) => setTimeout(resolve, ms));
    // Wait for all store actions to complete
    Promise.all(storePromises)
      .then(() => delay(5000)) // Wait for 5 seconds
      .then(() => {
        // After the delay, execute the next function
        
      })
      .catch((error) => {
        console.error("Error dispatching store actions:", error);
      });
        if (globalPermissionStatus("SLINE DISPUTE DASHBOARD")) {
          this.$store.dispatch("setCurrentRoute", "/freightForward/slineApprovalList");
          if (this.lastVisitPage && this.$sessionsStorage.get("lastVisitPage")) {
            this.fetchLastPage(this.lastVisitPage);
          } else {
            this.getSlineApprovalList("Normal", "0", "0", "1", "1");
          }
        } else {
          this.$router.push({
            path: "/dashboard",
          });
        }
   
  },
  data() {
    return {
      docURL: URL_BASE_DOC,
      selectDateFormat,
      isBusy: false,
      exportLoading: false,
      activeTab: 1, // Default to Pending (Index 0: All, 1: Pending, 2: Approved, 3: Rejected)
      items: [],
      totalRows: 0,
      currentPage: 1,
      perPage: 10,
      filter: "",
      filtersVisible: false,
      dateRange: { startDate: null, endDate: null },
      filters: {
        jobType: "",
        transportType: "",
      },
      fields: [
        { key: "SN", label: "SN", thClass: 'text-center', tdClass: 'text-center align-middle' },
        { key: "invoiceDetails", label: "Invoice Details", tdClass: 'align-middle' },
        { key: "jobDetails", label: "Job Details", tdClass: 'align-middle' },
        { key: "locDetails", label: "Location Details", tdClass: 'align-middle' },
        { key: "otherDetails", label: "Other Details", tdClass: 'align-middle' },
        { key: "people", label: "Users", tdClass: 'align-middle' },
        { key: "status", label: "Status", thClass: 'text-center', tdClass: 'text-center align-middle' },
        { key: "actions", label: "Actions", thClass: 'text-center', tdClass: 'text-center align-middle' },
      ],
      jobTypeOptions: [
        { value: "", text: "All" },
        { value: "FF", text: "FF" },
        { value: "CC", text: "CC" },
      ],
      transportOptions: [
        { value: "", text: "All" },
        { value: "Export", text: "Export" },
        { value: "Import", text: "Import" },
      ],
      debounce: null,
      confirmData: {
          item: null,
          action: '',
          title: '',
          message: '',
          variant: 'primary'
      },
      selectedJob: null,
    };
  },
  mounted() {
    this.initDateRange();
    this.getData();
  },
  methods: {
    moment,
    initDateRange() {
      const start = moment().startOf('year').toDate();
      const end = moment().endOf('year').toDate();
      this.dateRange = { startDate: start, endDate: end };
    },
    async getData() {
      this.isBusy = true;
      try {
        let status = "";
        if (this.activeTab === 1) status = "PENDING";
        else if (this.activeTab === 2) status = "APPROVED";
        else if (this.activeTab === 3) status = "REJECTED";

        const params = {
          page: this.currentPage,
          pageSize: this.perPage,
          status: status,
          jobType: this.filters.jobType,
          transportType: this.filters.transportType,
          createdAtRange: this.dateRange,
          search: this.filter,
        };

        const response = await SlineApprovalService.getSlineApprovalList(params);
        if (response.data.status) {
          this.items = response.data.data;
          this.totalRows = response.data.pagination.total;
        } else {
          this.items = [];
          this.totalRows = 0;
        }
      } catch (error) {
        console.error("Error fetching SLine approvals:", error);
      } finally {
        this.isBusy = false;
      }
    },
    async exportExcel() {
      if (this.exportLoading) return;
      this.exportLoading = true;
      try {
        let status = "";
        if (this.activeTab === 1) status = "PENDING";
        else if (this.activeTab === 2) status = "APPROVED";
        else if (this.activeTab === 3) status = "REJECTED";

        const params = {
          status: status,
          jobType: this.filters.jobType,
          transportType: this.filters.transportType,
          createdAtRange: JSON.stringify(this.dateRange),
          search: this.filter,
          exportExcel: "true",
        };

        const response = await SlineApprovalService.getSlineApprovalList(params);
        if (response.data.status && response.data.data.length > 0) {
          exportSlineApprovalListToExcel(response.data.data);
          if (this.$toasted) {
            this.$toasted.success("Excel exported successfully");
          }
        } else {
          if (this.$toasted) {
            this.$toasted.info("No data available to export");
          }
        }
      } catch (error) {
        console.error("Export Error:", error);
        if (this.$toasted) {
          this.$toasted.error("Failed to export Excel");
        }
      } finally {
        this.exportLoading = false;
      }
    },
    onPageChange(page) {
      this.currentPage = page;
      this.getData();
    },
    onTabChange(index) {
      this.activeTab = index;
      this.currentPage = 1;
      this.getData();
    },
    onFilterChange() {
      this.currentPage = 1;
      this.getData();
    },
    clearFilters() {
      this.filters = { jobType: "", transportType: "" };
      this.initDateRange();
      this.filter = "";
      this.onFilterChange();
    },
    debounceSearch() {
      clearTimeout(this.debounce);
      this.debounce = setTimeout(() => {
        this.onFilterChange();
      }, 500);
    },
    getStatusVariant(status) {
      switch (status) {
        case "APPROVED": return "success";
        case "REJECTED": return "danger";
        case "PENDING": return "warning";
        default: return "secondary";
      }
    },
    confirmAction(item, action) {
        this.confirmData = {
            item: item,
            action: action,
            title: `${action.charAt(0) + action.slice(1).toLowerCase()} Dispute`,
            message: `Are you sure you want to ${action.toLowerCase()} this dispute for Job: ${item.jobNo}?`,
            variant: action === 'APPROVE' ? 'success' : 'danger'
        };
        this.$refs['action-confirm-modal'].show();
    },
    async handleAction() {
        const { item, action } = this.confirmData;
        if (!item || !action) return;
        
        try {
            this.isBusy = true;
            const serviceCall = action === 'APPROVE' 
                ? SlineApprovalService.approveSline 
                : SlineApprovalService.rejectSline;
            
            const response = await serviceCall({ rsa_id: item.rsa_id });
            if (response.data.status) {
                this.getData();
            } else {
                alert(response.data.message || 'Action failed');
            }
        } catch (error) {
            console.error(`Error during ${action}:`, error);
        } finally {
            this.isBusy = false;
        }
    },
    viewDetails(item) {
        this.selectedJob = item;
        this.$refs['view-job-details-modal'].show();
    }
  },
};
</script>

<style scoped>
.approval-list-container {
  padding: 1rem;
}
.cell-content div {
  margin-bottom: 2px;
}
.extra-small {
  font-size: 0.7rem;
}
</style>



// WEBPACK FOOTER //
// SlineApprovalList.vue?f616b70c